import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import { IntlService } from '@progress/kendo-angular-intl';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SessionService } from '../core/session.service';
import { ICreateCustomerSession } from '../core/payment/create-customer-session.interface';
import { PaymentService } from '../core/payment/payment.service';
import { ICreateCustomerSessionResponse } from '../core/payment/create-customer-session-response.interface';
import { ISession } from '../core/session.interface';
import { LANGUAGES, LanguageService } from '../core/language.service';
import { STRINGS } from '../../strings';
import { ThemeService } from '../core/theme.service';
import { environment } from '../../environments/environment';
import {
  StripeElements,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { ICallbackResult } from '../core/callback-result.interface';
import { IPaymentConfirmationResponse } from '../core/payment/payment-confirmation-response.interface';
import {
  DialogComponent,
  DialogTitleBarComponent,
} from '@progress/kendo-angular-dialog';
import { SVGIcon, calendarIcon } from '@progress/kendo-svg-icons';
import {
  IPaymentScheduleOption,
  IPaymentScheduleInstallmentPreview,
} from '../core/payment/payment-schedule-option.interface';
import { IPremiumFinanceQuote } from '../core/payment/premium-finance-quote.interface';
import {
  CheckBoxState,
  CheckBoxComponent,
  FormFieldComponent,
  NumericTextBoxComponent,
  ErrorComponent,
} from '@progress/kendo-angular-inputs';
import { IPaymentAgreementResponse } from '../core/payment/payment-agreement-response.interface';
import { BehaviorSubject, EMPTY, filter, finalize, from, of } from 'rxjs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { NgIf, NgClass, AsyncPipe, NgFor, DatePipe } from '@angular/common';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { InputFieldComponent } from '../input-field.component';
import { LabelComponent } from '@progress/kendo-angular-label';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { PinVerificationService } from '../core/pin-verification/pin-verification.service';
import { KENDO_PDFEXPORT } from '@progress/kendo-angular-pdf-export';
import { TooltipComponent } from "../tooltip/tooltip.component";


@Component({
  selector: 'app-payment',
  templateUrl: './payment-stripe.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./payment-stripe.component.scss', './custom-styles.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    DialogComponent,
    DialogTitleBarComponent,
    SVGIconComponent,
    ButtonComponent,
    LoaderComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    InputFieldComponent,
    CheckBoxComponent,
    LabelComponent,
    AsyncPipe,
    DatePipe,
    FormFieldComponent,
    NumericTextBoxComponent,
    ErrorComponent,
    KENDO_PDFEXPORT,
    TooltipComponent
],
})
export class PaymentStripeComponent implements OnInit, AfterViewInit {
  @Input() embedId = '';
  @Input() amount = '0';
  numericAmount = 0;
  @Input() renderTarget = '';

  /* eslint-disable @angular-eslint/no-input-rename */
  @Input('callback') attrCallback: keyof typeof window = 'alert';
  @Input('policy-id') attrPolicyId = '';
  @Input('customer-id') attrCustomerId = '';
  @Input('first-name') attrFirstName = '';
  @Input('last-name') attrLastName = '';
  @Input('insured-name') attrInsuredName = '';
  @Input('email') attrEmail = '';
  @Input('address') attrAddress = '';
  @Input('city') attrCity = '';
  @Input('province') attrProvince = '';
  @Input('zip') attrZip = '';
  @Input('phone') attrPhone = '';
  @Input('custom-pif') attrCustomPif = '';
  @Input('custom-dp') attrCustomDp = '';
  @Input('custom-pmt') attrCustomPmt = '';
  @Input('custom-programcode') attrCustomProgramcode = '';
  @Input('custom-balancedue') attrCustomBalancedue = '';
  @Input('tnc-url') tncUrl = '';
  @Input('priv-url') privUrl = '';
  @Input('language') attrLanguage = '';
  @Input('reference-number') attrReferenceNumber = '';
  @Input('theme') theme = 'default';
  /* eslint-enable @angular-eslint/no-input-rename */
  @ViewChild('notificationContainer', { read: ViewContainerRef })
  public notificationContainer?: ViewContainerRef = undefined;
  @ViewChild('rootElement', { read: ViewContainerRef })
  public rootElement?: ViewContainerRef = undefined;

  public elementsOptions: StripeElementsOptions = {
    locale: 'en',
    fonts: [
      {
        cssSrc: 'https://embed.insurepay.com/inter.css',
      },
    ],

    appearance: {
      theme: 'flat',

      variables: {
        colorPrimary: '#002d75',
        colorBackground: '#fff',
        fontFamily: '"Inter", sans-serif',
        borderRadius: '0',
        colorText: '#212f29',
        fontSizeBase: '16px',
        fontSizeSm: '16px',
        gridRowSpacing: '32px',
        gridColumnSpacing: '16px',
        focusOutline: 'rgba(0, 0, 0, 0.16)',
        focusBoxShadow: '0 0 0 2px color-mix(in srgb, #3d3d3d 8%, transparent)',
      },

      rules: {
        '.Label': {
          marginBottom: '9px',
        },
        '.Input': {
          border: '1px solid #0000002A',
          paddingTop: '17px',
          paddingBottom: '17px',
        },
        '.Error': {
          marginBottom: '-22px',
        },
      },
    },
  };

  public acssAccountTypeOptions = [
    { text: 'Business', value: 'business' },
    { text: 'Personal', value: 'personal' },
  ];

    public paymentElementOptions: StripePaymentElementOptions = {
    terms: {
      card: 'never',
    },
  };

  public paymentIntentId: string = '';
  public confirmationToken: string = '';
  public otfAlreadyCharged: boolean = false;

  public stripeElements?: StripeElements;

  public dialogOpened$ = new BehaviorSubject<boolean>(false);
  public showVerifyPin$ = new BehaviorSubject<boolean>(false);
  public paymentLinkCompleted$ = new BehaviorSubject<boolean>(false);
  public paymentLinkCancelled$ = new BehaviorSubject<boolean>(false);
  private paymentLinkToken: string | undefined;
  public form: FormGroup = new FormGroup({
    amount: new FormControl(this.amount),
    referenceNumber: new FormControl(),
    acssAccountType: new FormControl('business'),
  });
  public amountForm: FormGroup = new FormGroup({
    amount: new FormControl(this.numericAmount),
  });
  public pinFormControls = {
    pin: new FormControl(),
    email: new FormControl(null, [
      Validators.required,
      this.checkEmailValidity(),
    ]),
  };
  public pinForm: FormGroup = new FormGroup(this.pinFormControls);
  public loading$ = new BehaviorSubject<boolean>(false);
  public complete = false;
  public stripeStep$ = new BehaviorSubject<boolean>(false);
  // Schedule selection (pay-in-full vs. plan vs. scheduled) now precedes the payment-method step,
  // so the choice is known before the SetupIntent/PAD mandate is created. Shown only when there is
  // a real choice (showScheduleStep()); otherwise the payment-method step follows client details
  // directly, as before.
  public scheduleStep$ = new BehaviorSubject<boolean>(false);
  public stripeFinal$ = new BehaviorSubject<boolean>(false);
  public blankStep$ = new BehaviorSubject<boolean>(environment.standalone);
  public receiptStep$ = new BehaviorSubject<boolean>(false);
  public acssSupported$ = new BehaviorSubject<boolean>(false);
  public session?: ISession = undefined;
  private _initSessionPromise?: Promise<void>;
  public clientInformation?: ISession['merchant']['clientInformation'] =
    undefined;
  public billingInformation?: ISession['merchant']['billingInformation'] =
    undefined;
  public originalPolicyId?: string = undefined;
  public policyId?: ISession['merchant']['policyId'] = undefined;
  // Effective field config the template binds to: a copy of policyId whose `required`
  // reflects the actual validator (Applied Epic drops the requirement), so the rendered
  // asterisk/`required` attribute matches whether a value is actually enforced.
  public policyIdFieldConfig?: ISession['merchant']['policyId'] = undefined;
  public paymentLabel?: ISession['merchant']['paymentLabel'] = undefined;
  public fee?: ISession['merchant']['fee'] = undefined;
  public feeAch?: ISession['merchant']['feeAch'] = undefined;
  public disclaimer?: ISession['merchant']['disclaimer'] = undefined;
  public bifurcationDisclaimer?: ISession['merchant']['fee']['text'] = undefined;
  public extraMessage?: ISession['merchant']['extraMessage'] = undefined;
  public extraFooterMessage?: ISession['merchant']['extraFooterMessage'] =
    undefined;
  public paymentWithToken?: boolean = false;
  public zip: any = undefined;

  public isEmailInvalid = false;

  public stashedPolicyId = '';
  public stashedCustom?: Record<string, any>;

  public environment = environment;

  public currentLanguage = LANGUAGES.english;

  public icons = {
    userIcon: {
      viewBox: '0 0 32 32',
      name: 'user-icon',
      content:
        '<path d="M24.52 19.4133C22.28 18.2667 19.3733 17.3333 16 17.3333C12.6267 17.3333 9.72001 18.2667 7.48001 19.4133C6.14668 20.0933 5.33334 21.4667 5.33334 22.96V24C5.33334 25.4667 6.53334 26.6667 8.00001 26.6667H24C25.4667 26.6667 26.6667 25.4667 26.6667 24V22.96C26.6667 21.4667 25.8533 20.0933 24.52 19.4133Z"/><path d="M13.04 16H18.96C20.5734 16 21.8134 14.5867 21.6 12.9867L21.1734 9.72001C20.76 7.18668 18.56 5.33334 16 5.33334C13.44 5.33334 11.24 7.18668 10.8267 9.72001L10.4 12.9867C10.1867 14.5867 11.4267 16 13.04 16Z"/>',
    },
    walletIcon: {
      viewBox: '0 0 32 32',
      name: 'wallet-icon',
      content:
        '<path d="M13.3333 21.3333V10.6667C13.3333 9.2 14.52 8 16 8H28V6.66667C28 5.2 26.8 4 25.3333 4H6.66667C5.18667 4 4 5.2 4 6.66667V25.3333C4 26.8 5.18667 28 6.66667 28H25.3333C26.8 28 28 26.8 28 25.3333V24H16C14.52 24 13.3333 22.8 13.3333 21.3333ZM17.3333 10.6667C16.6 10.6667 16 11.2667 16 12V20C16 20.7333 16.6 21.3333 17.3333 21.3333H29.3333V10.6667H17.3333ZM21.3333 18C20.2267 18 19.3333 17.1067 19.3333 16C19.3333 14.8933 20.2267 14 21.3333 14C22.44 14 23.3333 14.8933 23.3333 16C23.3333 17.1067 22.44 18 21.3333 18Z"/>',
    } as SVGIcon,
    tooltipIcon: {
      viewBox: '0 0 20 20',
      name: 'tooltip-icon',
      content:
        '<path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM9 14H11V16H9V14ZM10.61 4.04C8.55 3.74 6.73 5.01 6.18 6.83C6 7.41 6.44 8 7.05 8H7.25C7.66 8 7.99 7.71 8.13 7.33C8.45 6.44 9.4 5.83 10.43 6.05C11.38 6.25 12.08 7.18 12 8.15C11.9 9.49 10.38 9.78 9.55 11.03C9.55 11.04 9.54 11.04 9.54 11.05C9.53 11.07 9.52 11.08 9.51 11.1C9.42 11.25 9.33 11.42 9.26 11.6C9.25 11.63 9.23 11.65 9.22 11.68C9.21 11.7 9.21 11.72 9.2 11.75C9.08 12.09 9 12.5 9 13H11C11 12.58 11.11 12.23 11.28 11.93C11.3 11.9 11.31 11.87 11.33 11.84C11.41 11.7 11.51 11.57 11.61 11.45C11.62 11.44 11.63 11.42 11.64 11.41C11.74 11.29 11.85 11.18 11.97 11.07C12.93 10.16 14.23 9.42 13.96 7.51C13.72 5.77 12.35 4.3 10.61 4.04Z" />',
    },
    moneyIcon: {
      viewBox: '0 0 24 24',
      name: 'money-icon',
      content:
        '<path d="M7.73335 10.5333C4.70669 9.74667 3.73335 8.93333 3.73335 7.66667C3.73335 6.21333 5.08002 5.2 7.33335 5.2C9.22669 5.2 10.1734 5.92 10.52 7.06667C10.68 7.6 11.12 8 11.68 8H12.08C12.96 8 13.5867 7.13333 13.28 6.30667C12.72 4.73333 11.4134 3.42667 9.33335 2.92V2C9.33335 0.893333 8.44002 0 7.33335 0C6.22669 0 5.33335 0.893333 5.33335 2V2.88C2.74669 3.44 0.666687 5.12 0.666687 7.69333C0.666687 10.7733 3.21335 12.3067 6.93335 13.2C10.2667 14 10.9334 15.1733 10.9334 16.4133C10.9334 17.3333 10.28 18.8 7.33335 18.8C5.13335 18.8 4.00002 18.0133 3.56002 16.8933C3.36002 16.3733 2.90669 16 2.36002 16H1.98669C1.09335 16 0.466687 16.9067 0.80002 17.7333C1.56002 19.5867 3.33335 20.68 5.33335 21.1067V22C5.33335 23.1067 6.22669 24 7.33335 24C8.44002 24 9.33335 23.1067 9.33335 22V21.1333C11.9334 20.64 14 19.1333 14 16.4C14 12.6133 10.76 11.32 7.73335 10.5333Z" />',
    },
    calendarIcon,
  };
  public logo = 'https://embed.insurepay.com/insurepay-logo.png';

  public stripeFee = -1;
  public stripeAmountCents = -1;
  public isAgreementChecked = false;
  public isBifurcatedAgreementChecked = false;
  public canFinalizePayment$ = new BehaviorSubject<boolean>(false);
  public showSpeedbump$ = new BehaviorSubject<boolean>(
    this.environment.standalone,
  );
  public brand: string = '';
  public identifier: string = '';
  public transactionId: string = '';
  public transactionDate: string = 'date';
  public isBifurcated: boolean = false;
  public paymentType: string = '';
  private pinId: string = '';
  private customerSessionData:
    | {
        address?: string;
        amount: number;
        city?: string;
        custom?: object;
        customerId?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        insuredName?: string;
        invoiceNumber?: string;
        policyId?: string;
        province?: string;
        referenceNumber?: string;
        zip?: string;
        isAppliedEpic?: boolean;
        forwardReceipt?: boolean;
        forwardReceiptEmail?: string;
        source?: string;
        createdBy?: string;
        acssAccountType?: string;
      }
    | undefined;
  public isBlank: boolean = environment.standalone;
  public notificationTracker:  { [key: string]: any } = {};

  // PPD-1321 — Applied Epic broker preview: copy-URL bar
  public isAppliedEpic$ = new BehaviorSubject<boolean>(false);
  public copyText = 'Copy Payment URL';

  public paymentScheduleOptions: IPaymentScheduleOption[] = [];
  public selectedPaymentScheduleRuleId: string | null = null;
  public paymentScheduleLoading$ = new BehaviorSubject<boolean>(false);
  public paymentSchedulePreview: IPaymentScheduleInstallmentPreview[] = [];

  // PPD-629 — "Schedule payment in full". Sentinel id distinguishes the option
  // from "Pay in Full" (null) and from real rule ObjectIds. When selected, a
  // native <input type="date"> bounded by the merchant's window is shown and the
  // chosen date is sent to the backend instead of a rule id. A native date input
  // (not kendo-datepicker) is used deliberately: this component renders in a
  // ShadowDom, where Kendo's popup-based controls don't open/style correctly —
  // the same reason the schedule dropdown is a native <select>. All three values
  // are yyyy-MM-dd strings ('' = none) bound to the input's value/min/max.
  public readonly SCHEDULE_FULL_ID = '__SCHEDULE_FULL__';
  public scheduledPaymentDate = '';
  public scheduleMinDate = '';
  public scheduleMaxDate = '';

  // True once a "scheduled" agreement succeeds — the receipt view then shows a
  // simple "payment scheduled" confirmation instead of the charge receipt (no
  // money moved at checkout).
  public isScheduledPayment = false;

  // PPD-1300 — premium finance offer card.
  // Set once the FIRSTLink indicative quote is fetched successfully for an RTP link
  // that has premiumFinance.enabled = true. Null means PF is not available for this link.
  public premiumFinanceQuote: IPremiumFinanceQuote | null = null;
  // Active when the user is on the payment-method-type selection / PF card step.
  public pfStep$ = new BehaviorSubject<boolean>(false);
  // Tracks which payment method type the user has chosen within the PF selection step.
  public selectedPaymentMethodType: 'stripe' | 'premiumFinance' = 'stripe';

  constructor(
    private sessionService: SessionService,
    private paymentService: PaymentService,
    private pinVerificationService: PinVerificationService,
    private notificationService: NotificationService,
    private languageService: LanguageService,
    private intl: IntlService,
    private themeService: ThemeService,
    private stripe: StripeService,
    private route: ActivatedRoute,
  ) {
    this.paymentScheduleOptions = [this.buildPayInFullOption()];
  }

  public callback = (_: ICallbackResult | undefined) => {};

  public ngOnInit(): void {
    if (environment.standalone && this.route.snapshot.data['rtpPayment']) {
      this.blankStep$.next(false);
      this.showSpeedbump$.next(false);
      this.isBlank = false;
    }

    if (this.renderTarget !== '') {
      const button = document.getElementById(
        this.renderTarget,
      ) as HTMLButtonElement;

      button.addEventListener('click', () =>
        // @ts-ignore
        this.open({}, window[this.attrCallback]),
      );
    }

    if (this.environment.standalone) {
      if (this.route.snapshot.data['blankPayment']) {
        this.embedId = this.route.snapshot.paramMap.get('shortName') || '';
      } else {
        this.embedId = this.route.snapshot.paramMap.get('token') || '';
        this.paymentLinkToken = this.embedId || undefined;
      }
    }

    this.loading$.next(true);
    if (this.environment.standalone) {
      if (this.route.snapshot.data['blankPayment']) {
        this.sessionService.initBlankSession(this.embedId);
      } else {
        this.sessionService.initRtpSession(this.embedId);
      }
    } else {
      this.sessionService.initSession(this.embedId);
    }

    this.sessionService.getPaymentLinkStatus().subscribe((status) => {
      if (status === 'Paid') {
        this.paymentLinkCompleted$.next(true);
        this.loading$.next(false);
      } else if (status === 'Cancelled' || status === 'Expired') {
        this.paymentLinkCancelled$.next(true);
        this.loading$.next(false);
      }
    });

    this.sessionService.getSession().subscribe((session) => {
      if (session) {
        this.initSession(session);
      }
    });
    this.form
      .get('amount')
      ?.setValue(this.intl.parseNumber(this.amount.toString()).toFixed(2));
    this.form.get('policyId')?.setValue(this.stashedPolicyId);

    // @ts-ignore
    window.acordEmbed = {
      open: this.open.bind(this),
    };

    const readyEvent = new Event('acordembedready');
    document.dispatchEvent(readyEvent);
  }

  public ngAfterViewInit() {
    if (this.rootElement) {
      this.themeService.loadTheme(this.rootElement, this.theme);
      let element;
      if (this.environment.standalone) {
        element = window.document.getElementsByTagName('app-payment')[0];
      } else {
        element = window.document.getElementsByTagName('acord-payment')[0];
      }

      const stripeContainer = window.document.createElement('div');
      stripeContainer.slot = 'stripe-container';

      const stripePaymentContainer = window.document.createElement('div');
      stripePaymentContainer.id = 'stripe-payment-container';

      const stripeLinkContainer = window.document.createElement('div');
      stripeLinkContainer.id = 'stripe-link-container';

      stripeContainer.appendChild(stripeLinkContainer);
      stripeContainer.appendChild(stripePaymentContainer);

      element.appendChild(stripeContainer);
    }

    if (environment.standalone && this.route.snapshot.data['blankPayment']) {
      const recaptchaContainer = window.document.createElement('div');
      recaptchaContainer.slot = 'recaptcha-container';

      const recaptchaTarget = window.document.createElement('div');
      recaptchaTarget.id = 'recaptcha-target';

      recaptchaContainer.appendChild(recaptchaTarget);

      const element = window.document.getElementsByTagName('app-payment')[0];

      element.appendChild(recaptchaContainer);

      if (this.environment.standalone) this.setupRecaptcha();
    }
  }

  private setupRecaptcha() {
    // @ts-ignore
    if (!window.isRecaptchaReady) {
      setTimeout(() => this.setupRecaptcha(), 1000);
      return;
    }

    // @ts-ignore
    window.grecaptcha.render(
      window.document.getElementById('recaptcha-target'),
      {
        sitekey: environment.recaptchaSiteKey,
        callback: () => this.checkAgreements(),
        'expired-callback': () => this.checkAgreements(),
        'error-callback': () => this.checkAgreements(),
      },
    );
  }

  private getRecaptchaValue(): string | undefined {
    // @ts-ignore
    return window.grecaptcha.getResponse();
  }

  private resetRecaptcha(): void {
    // @ts-ignore
    window.grecaptcha.reset();
  }

  public getString(key: keyof typeof STRINGS, ...args: Array<string>) {
    return this.languageService.getString(key, ...args);
  }

  public checkEmailValidity(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value !== 'string') {
        return { emailInvalid: 'Email is required' };
      }

      const value = control.value;

      if (value.includes('@') && value.includes('.')) {
        const atCount = value.split('@').length;
        const dotCount = value.split('.').length;

        if (atCount > 2 || dotCount > 2) {
          // Semicolon splits email addresses, should be one greater @ and . than ;
          if (!value.includes('@@') && !value.includes('..')) {
            this.isEmailInvalid = false;
            return null;
          }
        } else {
          this.isEmailInvalid = false;
          return null;
        }
      }

      this.isEmailInvalid = true;
      return { emailInvalid: 'Invalid email address' };
    };
  }

  public initSession(session?: ISession): Promise<void> {
    if (!this._initSessionPromise) {
      this._initSessionPromise = this._doInitSession(session);
    }
    return this._initSessionPromise;
  }

  private async _doInitSession(session?: ISession) {
    if (!session) {
      await new Promise((res, _rej) => {
        this.sessionService.getSession().subscribe((newSession) => {
          if (newSession) {
            session = newSession;
            res(session);
          }
        });
      });
    }

    session = session as ISession;

    this.stripe.setKey(
      session.region === 'USA'
        ? environment.stripeKeyUS
        : environment.stripeKeyCA,
    );

    if (session.region === 'CAN') {
      await new Promise(res => {
        this.paymentService.isAcssSupported().subscribe(acssSupported => {
          res(acssSupported);
          this.acssSupported$.next(acssSupported);
        });
      });
    }

    this.session = session;
    this.clientInformation = session.merchant.clientInformation;
    this.billingInformation = session.merchant.billingInformation;
    this.policyId = session.merchant.policyId;
    this.paymentLabel = session.merchant.paymentLabel;
    this.fee = session.merchant.fee;
    this.feeAch = session.merchant.feeAch;
    this.disclaimer = session.merchant.disclaimer;
    this.bifurcationDisclaimer = session.merchant.fee.text;
    this.extraMessage = session.merchant.extraMessage;
    this.extraFooterMessage = session.merchant.extraFooterMessage;
    this.paymentWithToken = session?.customerSession?.paymentWithToken || false;

    if (environment.standalone && this.route.snapshot.data['rtpPayment']) {
      this.customerSessionData = session.customerSession;
    }

    // PPD-1321: broker lands via ?fromEpic=true redirect; client follows the clean URL
    this.isAppliedEpic$.next(new URLSearchParams(window.location.search).get('fromEpic') === 'true');

    const formGroupConfig: { [key: string]: FormControl } = {
      amount: new FormControl(this.amount.toString()),
      referenceNumber: new FormControl(),
      acssAccountType: new FormControl('business'),
    };

    // Applied Epic payments can be made on-account without a policy number (e.g. a custom
    // "pay another amount" deposit), so don't force the policy field for those sessions even
    // when the merchant normally requires it.
    const isAppliedEpic = !!this.customerSessionData?.isAppliedEpic;
    const policyIdRequired = !!this.policyId.required && !isAppliedEpic;

    // Keep the rendered field's required state in sync with the validator below.
    this.policyIdFieldConfig = { ...this.policyId, required: policyIdRequired };

    if (this.policyId.show || this.policyId.required) {
      formGroupConfig.policyId = policyIdRequired
        ? // pattern(/\S/) rejects whitespace-only input that Validators.required alone accepts.
          new FormControl(null, [Validators.required, Validators.pattern(/\S/)])
        : new FormControl();
    }

    Object.entries(this.clientInformation).forEach(([key, value]) => {
      if (typeof value === 'boolean') return;

      if (value.show || value.required) {
        if (key === 'email') {
          formGroupConfig[key] = new FormControl(null, [
            Validators.required,
            this.checkEmailValidity(),
          ]);
        } else {
          formGroupConfig[key] = value.required
            ? new FormControl(null, [Validators.required])
            : new FormControl();
        }
      }
    });

    this.form = new FormGroup(formGroupConfig);

    if (environment.standalone && this.route.snapshot.data['rtpPayment']) {
      if (this.customerSessionData?.amount) {
        this.form.get('amount')?.setValue(this.customerSessionData.amount.toString());
        this.amount = this.customerSessionData.amount.toString();
      }

      if (this.customerSessionData?.policyId) {
        if (this?.policyId?.omit.allow && this.policyId?.omit.quantity) {
          this.originalPolicyId = this.customerSessionData.policyId as string;
          this.customerSessionData.policyId =
            this.customerSessionData.policyId.slice(
              this.policyId.omit.quantity,
            );
        }
        this.form.get('policyId')?.setValue(this.customerSessionData.policyId);
        this.stashedPolicyId = this.customerSessionData.policyId;
      }

      if (this.customerSessionData?.custom) {
        this.stashedCustom = this.customerSessionData.custom;
        this.form.addControl(
          'custom',
          new FormControl(this.customerSessionData.custom),
        );
      }

      this.customerSessionData?.customerId &&
        this.form
          .get('customerId')
          ?.setValue(this.customerSessionData.customerId);
      this.customerSessionData?.referenceNumber &&
        this.form
          .get('referenceNumber')
          ?.setValue(this.customerSessionData.referenceNumber);
      this.customerSessionData?.invoiceNumber &&
        this.form
          .get('invoiceNumber')
          ?.setValue(this.customerSessionData.invoiceNumber);
      this.customerSessionData?.firstName &&
        this.form
          .get('firstName')
          ?.setValue(this.customerSessionData.firstName);
      this.customerSessionData?.lastName &&
        this.form.get('lastName')?.setValue(this.customerSessionData.lastName);
      this.customerSessionData?.insuredName &&
        this.form
          .get('insuredName')
          ?.setValue(this.customerSessionData.insuredName);
      this.customerSessionData?.email &&
        this.form.get('email')?.setValue(this.customerSessionData.email);
      this.customerSessionData?.address &&
        this.form.get('address')?.setValue(this.customerSessionData.address);
      this.customerSessionData?.city &&
        this.form.get('city')?.setValue(this.customerSessionData.city);
      this.customerSessionData?.province &&
        this.form.get('province')?.setValue(this.customerSessionData.province);
      this.customerSessionData?.zip &&
        this.form.get('zip')?.setValue(this.customerSessionData.zip);

      this.customerSessionData?.isAppliedEpic &&
        this.form.addControl(
          'isAppliedEpic',
          new FormControl(this.customerSessionData.isAppliedEpic),
        );
    }

    // PPD-1300 — fetch the FIRSTLink quote when this RTP link has PF enabled.
    // Fetched in the background so it's ready when the user reaches the payment method step.
    // PF option is silently hidden if the quote is unavailable.
    if (
      environment.standalone &&
      this.route.snapshot.data['rtpPayment'] &&
      session.customerSession?.premiumFinance?.enabled &&
      this.paymentLinkToken
    ) {
      this.paymentService.getPremiumFinanceQuote(this.paymentLinkToken).subscribe(quote => {
        if (quote?.available) {
          this.premiumFinanceQuote = quote;
        }
      });
    }

    this.loadPaymentScheduleRules();
    this.loading$.next(false);
  }

  public createCustomerSession(): void {
    this.form.markAllAsTouched();
    this.loading$.next(true);

    if (this.form.invalid) {
      this.loading$.next(false);
      return;
    }

    const results = this.form.value as ICreateCustomerSession;

    results.forwardReceipt = this.customerSessionData?.forwardReceipt || false;
    results.forwardReceiptEmail = this.customerSessionData?.forwardReceiptEmail || '';
    results.source = this.customerSessionData?.source || 'blank';

    // The Applied Epic / policy-lookup flow doesn't render a customerId field, so when the
    // form has no customerId control, carry the value from the stored session — the receipt
    // step needs it to resolve the account's agency/branch. When the control IS present we
    // leave results.customerId as the user entered it (including an intentionally cleared
    // value). (isAppliedEpic already round-trips via the control added in _doInitSession.)
    if (!this.form.contains('customerId')) {
      results.customerId = this.customerSessionData?.customerId;
    }

    if (this?.policyId?.omit.allow && this.policyId?.omit.quantity) {
      results.policyId = this.originalPolicyId || results.policyId;
    }

    results.createdBy = this.customerSessionData?.createdBy || false;

    results.isTokenize = false;
    results.paymentWithToken = this.paymentWithToken;

    // Carry the plan choice so the backend creates the ACSS/PAD mandate with the right
    // payment_schedule. Only a multi-installment plan is recurring ("combined"); pay-in-full AND
    // "schedule payment in full" (a single deferred payment) are one-time ("sporadic"), so both
    // send a null rule id. The chosen future date is sent later, at the agreement step.
    const isScheduled = this.selectedPaymentScheduleRuleId === this.SCHEDULE_FULL_ID;
    results.paymentScheduleRuleId = isScheduled ? null : this.selectedPaymentScheduleRuleId;

    if (environment.standalone && this.route.snapshot.data['blankPayment']) {
      results.verificationId = this.pinId;
    }

    this.paymentService
      .createCustomerSession(results)
      .pipe(
        mergeMap((result) => {
          if (!result?.clientSecret) {
            this.callback({
              status: 'ERROR',
              message: 'Unknown error occurred, please try again',
              errorType: 'SYSTEM',
            });
            throw new Error('Unable to setup stripe customer session');
          }

          return of(result as ICreateCustomerSessionResponse);
        }),
        mergeMap((result) => {
          this.paymentIntentId = result.paymentIntentId;
          this.otfAlreadyCharged = result.otfAlreadyCharged === true;
          this.elementsOptions.clientSecret = result.clientSecret;
          this.elementsOptions.customerSessionClientSecret =
            result.customerSessionClientSecret;

          return of(this.elementsOptions);
        }),
        mergeMap((elementsOptions) => this.stripe.elements(elementsOptions)),
        tap((elements) => {
          const paymentElement = elements.create(
            'payment',
            this.paymentElementOptions,
          );

          paymentElement.mount('#stripe-payment-container');

          this.stripeElements = elements;
          // Reveal the payment-method step, then collapse the schedule step. Both emissions are
          // synchronous so Angular batches them into one change-detection pass; ordering it this way
          // also guards against ever rendering a transient "both steps collapsed" state that would
          // briefly un-collapse the client-details form. The schedule step's loader stays visible
          // until this point (no flicker back to client details).
          this.stripeStep$.next(true);
          this.scheduleStep$.next(false);
          this.pfStep$.next(false);
        }),
        catchError((err) => {
          this.displayNotification(
            err?.message || err || 'unknown error, please try again later',
          );
          console.error(err?.message || err);
          this.stripeStep$.next(false);

          throw err;
        }),
        finalize(() => {
          this.loading$.next(false);
        }),
      )
      .subscribe(() => console.log('customer session created'));
  }

  public submitAmount(event: Event) {
    event.preventDefault();

    this.amount = this.amountForm.value.amount.toString();
    this.form.controls.amount.setValue(this.amount);

    this.blankStep$.next(false);
  }

  public showEditAmount(event: Event) {
    event.preventDefault();

    this.blankStep$.next(true);
    this.stripeStep$.next(false);
  }

  public async submitAgreement(event: Event) {
    event.preventDefault();

    // PPD-629 — "Schedule payment in full" defers the charge to a chosen date.
    // Send that date (not a rule id); require it to be picked first.
    const isScheduled = this.selectedPaymentScheduleRuleId === this.SCHEDULE_FULL_ID;
    if (isScheduled && !this.scheduledPaymentDate) {
      this.displayNotification(this.getString('scheduleDateRequired'));
      return;
    }

    this.loading$.next(true);

    this.paymentService
      .paymentAgreement({
        paymentIntentId: this.paymentIntentId,
        confirmationToken: this.confirmationToken,
        recaptcha:
          this.environment.standalone &&
          this.route.snapshot.data['blankPayment']
            ? this.getRecaptchaValue()
            : '',
        verificationId:
          this.environment.standalone &&
          this.route.snapshot.data['blankPayment']
            ? this.pinId
            : '',
        paymentScheduleRuleId: isScheduled ? null : this.selectedPaymentScheduleRuleId,
        scheduledDate: isScheduled ? (this.scheduledPaymentDate || null) : null,
        paymentLinkToken: this.paymentLinkToken,
      })
      .pipe(
        mergeMap((result) => {
          if (!result?.status) {
            this.displayNotification('unknown error, please try again later');
            this.stripeFinal$.next(false);
            throw new Error('could not submit agreements');
          }

          return of(result as IPaymentAgreementResponse);
        }),
        mergeMap((result) => {
          if (result.status === 'requires_action' && result.clientSecret) {
            return from(
              this.stripe.handleNextAction({
                clientSecret: result.clientSecret,
              }),
            ).pipe(
              mergeMap((nextActionResult) => {
                if (nextActionResult?.error) {
                  this.displayNotification(
                    nextActionResult.error.message ||
                      'unknown error, please try again later',
                  );
                  this.stripeFinal$.next(false);
                  throw new Error('failed completing 3DS for payment');
                }

                return this.paymentService.completeAgreement(
                  result.paymentIntent,
                  this.paymentIntentId,
                  this.paymentLinkToken,
                );
              }),
              mergeMap((completeResult) => {
                if (!completeResult?.status) {
                  this.displayNotification(
                    'unknown error, please try again later',
                  );
                  this.stripeFinal$.next(false);
                  throw new Error('could not complete agreement after 3DS');
                }
                return of(completeResult as IPaymentAgreementResponse);
              }),
            );
          }

          return of(result);
        }),
        mergeMap((result) => {
          if (
            result.status === 'requires_fee_action' &&
            result.feeClientSecret
          ) {
            return from(
              this.stripe.handleNextAction({
                clientSecret: result.feeClientSecret,
              }),
            ).pipe(
              mergeMap((nextActionResult) => {
                // The premium PI was already captured server-side before the fee's
                // 3DS was deferred. We must call complete-fee on BOTH outcomes:
                //  - success -> the server captures the fee, then we continue below.
                //  - failure -> the server refunds/cancels the premium so the
                //    customer isn't left charged with no fee collected.
                // Previously a failed fee 3DS threw here without calling the server,
                // stranding the captured premium as a phantom charge.
                const feeResult$ = this.paymentService.completeAgreementFee(
                  result.paymentIntent,
                  this.paymentIntentId,
                  result.feePaymentIntent!,
                  this.paymentLinkToken,
                );

                if (nextActionResult?.error) {
                  const message =
                    nextActionResult.error.message ||
                    'unknown error, please try again later';
                  return feeResult$.pipe(
                    // Server has undone the premium; surface the 3DS failure and end
                    // the flow (EMPTY) without treating it as a completed payment.
                    mergeMap(() => {
                      this.displayNotification(message);
                      this.stripeFinal$.next(false);
                      return EMPTY;
                    }),
                  );
                }

                return feeResult$;
              }),
              mergeMap((feeCompleteResult) => {
                if (!feeCompleteResult?.status) {
                  this.displayNotification(
                    'unknown error, please try again later',
                  );
                  this.stripeFinal$.next(false);
                  throw new Error('could not complete fee after 3DS');
                }
                return of(feeCompleteResult as IPaymentAgreementResponse);
              }),
            );
          }

          return of(result);
        }),
        tap((result) => {
          if (
            result.status === 'succeeded' ||
            result.status === 'processing' ||
            result.status === 'scheduled'
          ) {
            // Sync the receipt's amount + fee from what the back-end actually
            // charged. For a plan-selected checkout this is the first-
            // installment amount, not the policy total set during SetupIntent
            // confirm. Guard against missing fields so an older API stays
            // backwards-compatible.
            if (typeof result.amountCents === 'number') {
              this.stripeAmountCents = result.amountCents;
            }
            if (typeof result.feeCents === 'number') {
              this.stripeFee = result.feeCents;
            }
            this.callback({
              status: 'SUCCESS',
              message: 'APPROVED',
              transactionId: result.paymentIntent,
            });
            // Scheduled payments aren't charged at checkout — the receipt view
            // shows a "payment scheduled" confirmation rather than a charge receipt.
            this.isScheduledPayment = result.status === 'scheduled';
            this.transactionId = result.paymentIntent;
            this.transactionDate = new Date().toISOString();
            this.close(true);
          } else {
            this.displayNotification(
              result?.detail || 'unknown error, please try again later',
            );
            this.stripeFinal$.next(false);
          }
        }),
        finalize(() => {
          this.loading$.next(false);
          this.resetRecaptcha();
        }),
      )
      .subscribe(() => console.log('agreement submitted'));
  }

  private buildPayInFullOption(): IPaymentScheduleOption {
    const thisComponent = this;

    return {
      id: null,
      get name(): string {
        return thisComponent.getString('paymentScheduleInFullOption');
      },
    } as IPaymentScheduleOption;
  }

  // PPD-629 — "Schedule payment in full". No cadence/count, so the installment
  // preview table (which requires count >= 2) stays empty for this option.
  private buildScheduleInFullOption(): IPaymentScheduleOption {
    const thisComponent = this;

    return {
      id: this.SCHEDULE_FULL_ID,
      get name(): string {
        return thisComponent.getString('scheduleInFullOption');
      },
    } as IPaymentScheduleOption;
  }

  private loadPaymentScheduleRules(): void {
    if (!this.sessionService.getCurrentSession()) { return; }
    this.paymentScheduleLoading$.next(true);
    this.paymentService.getActivePaymentScheduleRules().subscribe({
      next: (rules) => {
        // Order per AC#3: Pay in Full, Schedule payment in full, then plans.
        this.paymentScheduleOptions = [...this.baseScheduleOptions(), ...rules];
        this.selectedPaymentScheduleRuleId = null;
        this.paymentScheduleLoading$.next(false);
      },
      error: () => {
        this.paymentScheduleOptions = this.baseScheduleOptions();
        this.selectedPaymentScheduleRuleId = null;
        this.paymentScheduleLoading$.next(false);
        this.displayNotification(this.getString('paymentScheduleLoadFailed'));
      },
    });
  }

  // PPD-629 — both flags must be on for the merchant to offer scheduling.
  private isFuturePaymentEnabled(): boolean {
    const fp = this.session?.merchant?.futurePayment;
    return !!fp && fp.featureEnabled === true && fp.enabled === true;
  }

  private baseScheduleOptions(): IPaymentScheduleOption[] {
    const options: IPaymentScheduleOption[] = [this.buildPayInFullOption()];
    // Only offer scheduling when the merchant is enabled AND there is at least one
    // selectable date. The latter hides the option when, e.g., the merchant uses
    // "end of the month" and today IS the last day of the month (the only in-window
    // date would be today, which is not selectable).
    if (this.isFuturePaymentEnabled() && this.hasSelectableScheduleDate()) {
      options.push(this.buildScheduleInFullOption());
    }
    return options;
  }

  // Shared min/max window: min = tomorrow; max = end of the current month
  // (endOfMonth mode) or today + maxDaysOut. Source of truth for both the picker
  // bounds and whether the option is offered at all.
  private scheduleDateBounds(): { min: Date; max: Date } {
    const fp = this.session?.merchant?.futurePayment;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const min = new Date(today);
    min.setDate(min.getDate() + 1);

    let max: Date;
    if (fp?.endOfMonth) {
      max = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else {
      max = new Date(today);
      max.setDate(max.getDate() + Math.max(0, fp?.maxDaysOut ?? 0));
    }

    return { min, max };
  }

  private hasSelectableScheduleDate(): boolean {
    const { min, max } = this.scheduleDateBounds();
    return max.getTime() >= min.getTime();
  }

  public onPaymentScheduleChange(value: string | null): void {
    this.selectedPaymentScheduleRuleId = value;
    if (value === this.SCHEDULE_FULL_ID) {
      // Future single payment: bound the picker, clear any plan preview.
      this.computeScheduleDateBounds();
      this.scheduledPaymentDate = '';
      this.paymentSchedulePreview = [];
    } else {
      this.scheduledPaymentDate = '';
      this.recomputeSchedulePreview();
    }
  }

  // Native <input type="date"> emits a yyyy-MM-dd string (or '' when cleared).
  public onScheduledDateChange(event: Event): void {
    this.scheduledPaymentDate = (event.target as HTMLInputElement).value;
  }

  // PPD-629 — the payment-schedule step is only meaningful when there is a real
  // choice beyond Pay in Full (an active plan rule, or an available future single
  // payment). When Pay-in-Full is the only option the step is skipped entirely —
  // e.g. no plan rules AND future payments disabled, or end-of-month on the last
  // day of the month (the future option is hidden, leaving only Pay in Full).
  public showScheduleStep(): boolean {
    return this.paymentScheduleOptions.length > 1;
  }

  // Client-details "Save details" submit. The schedule choice must be made before the SetupIntent
  // (and its PAD mandate) is created, so when a schedule step is offered we go there first;
  // otherwise we create the session and go straight to the payment-method step (legacy behavior).
  public proceedFromClientDetails(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    if (this.showScheduleStep()) {
      this.recomputeSchedulePreview();
      this.scheduleStep$.next(true);
    } else if (this.premiumFinanceQuote) {
      this.pfStep$.next(true);
    } else {
      this.createCustomerSession();
    }
  }

  // Schedule-step "Next" submit → create the session (carrying the selection) and advance to the
  // payment-method step. Re-runnable: editing the schedule later re-creates the session/mandate.
  public proceedFromSchedule(event: Event): void {
    event.preventDefault();

    if (this.selectedPaymentScheduleRuleId === this.SCHEDULE_FULL_ID && !this.scheduledPaymentDate) {
      this.displayNotification(this.getString('scheduleDateRequired'));
      return;
    }

    // PPD-1300 — when PF is available, show the payment method type selection before
    // creating the Stripe customer session (which would be unnecessary for PF).
    if (this.premiumFinanceQuote) {
      this.scheduleStep$.next(false);
      this.pfStep$.next(true);
      return;
    }

    // Keep the schedule step active (its "Next" button shows the loader) while the session is
    // created. It is collapsed only once the payment-method step is ready — see createCustomerSession
    // — so the client-details section never briefly re-expands during the round-trip.
    this.createCustomerSession();
  }

  // PPD-1300 — user chose "Standard Payment" in the PF dropdown; collapse the PF selection
  // step and proceed to create the Stripe customer session.
  public proceedFromPfSelection(): void {
    this.createCustomerSession();
  }

  // PPD-1300 — called when the user switches between "Standard Payment" and "Premium Finance"
  // in the payment method type dropdown.
  public onPaymentMethodTypeChange(value: string): void {
    this.selectedPaymentMethodType = value as 'stripe' | 'premiumFinance';
  }

  // "Edit" on the schedule section: collapse the later steps and reopen the schedule step.
  // Proceeding again re-creates the session so the mandate matches the new choice.
  public showSchedule(event: Event): void {
    event.preventDefault();
    if (this.loading$.value) return;

    this.stripeStep$.next(false);
    this.stripeFinal$.next(false);
    this.pfStep$.next(false);
    this.selectedPaymentMethodType = 'stripe';
    this.stripeFee = -1;
    this.stripeAmountCents = -1;
    this.scheduleStep$.next(true);
    this.recomputeSchedulePreview();
    this.checkAgreements();
  }

  // Step number for the schedule section (shown only when showScheduleStep()): right after the
  // client-details step (1, or 2 in standalone "blank" mode which prepends the amount step).
  public scheduleSectionNumber(): number {
    return this.isBlank ? 3 : 2;
  }

  // Payment-method step number: after client details, shifted down by one when the schedule step
  // is shown.
  public paymentMethodSectionNumber(): number {
    return (this.isBlank ? 3 : 2) + (this.showScheduleStep() ? 1 : 0);
  }

  // Payment Summary's section number shifts up by one when the schedule step is
  // shown, so hiding the step doesn't leave a gap (e.g. 1, 2, 4). Base is 3
  // (embedded) / 4 (standalone "blank" mode adds the amount step up front).
  public summarySectionNumber(): number {
    return (this.isBlank ? 4 : 3) + (this.showScheduleStep() ? 1 : 0);
  }

  // Mirrors backend FuturePaymentDatePolicy: min = tomorrow; max = end of the
  // current month (when endOfMonth) or today + maxDaysOut. Stored as yyyy-MM-dd
  // strings for the native date input's min/max attributes.
  private computeScheduleDateBounds(): void {
    const { min, max } = this.scheduleDateBounds();
    this.scheduleMinDate = this.toDateInputValue(min);
    this.scheduleMaxDate = this.toDateInputValue(max);
  }

  // Local yyyy-MM-dd (no UTC shift) — the backend re-validates and pins to UTC.
  private toDateInputValue(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private reset() {
    this.stripeStep$.next(false);
    this.scheduleStep$.next(false);
    this.stripeFinal$.next(false);
    this.pfStep$.next(false);
    this.selectedPaymentMethodType = 'stripe';
    this.stripeFee = -1;
    this.stripeAmountCents = -1;
    this.paymentScheduleOptions = [
      this.buildPayInFullOption(),
      ...this.paymentScheduleOptions.filter((option) => option.id !== null),
    ];
    this.selectedPaymentScheduleRuleId = null;
    this.paymentSchedulePreview = [];
    this.scheduledPaymentDate = '';
    this.isScheduledPayment = false;
  }

  /**
   * Mirrors backend PaymentLedgerService.CreateLedger exactly: equal-cents per
   * installment with the remainder applied to the last; first due date = today,
   * subsequent dates +7/+14 days or +1/+3 calendar months matching .NET
   * DateTime.AddMonths semantics (day-of-month clamped to target month length).
   * Returns [] when no plan is selected, the option has no cadence/count, or
   * stripeAmountCents is not yet known.
   */
  private recomputeSchedulePreview(): void {
    if (this.selectedPaymentScheduleRuleId === null) {
      this.paymentSchedulePreview = [];
      return;
    }

    const option = this.paymentScheduleOptions.find(
      (o) => o.id === this.selectedPaymentScheduleRuleId,
    );

    // On the schedule step (before confirmation) the fee-adjusted total isn't known yet, so show an
    // estimate from the entered amount; after confirmation stripeAmountCents holds the exact total.
    const totalCents = this.stripeAmountCents > 0 ? this.stripeAmountCents : this.getAmountCents();
    const n = option?.installmentCount ?? 0;
    if (!option || !option.cadence || n < 2 || totalCents <= 0) {
      this.paymentSchedulePreview = [];
      return;
    }

    const perInstallment = Math.floor(totalCents / n);
    const remainder = totalCents - perInstallment * n;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const preview: IPaymentScheduleInstallmentPreview[] = [];
    for (let i = 1; i <= n; i++) {
      preview.push({
        number: i,
        dueDate: this.addCadence(today, option.cadence, i - 1),
        amountCents: i === n ? perInstallment + remainder : perInstallment,
      });
    }
    this.paymentSchedulePreview = preview;
  }

  private addCadence(
    base: Date,
    cadence: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY',
    periods: number,
  ): Date {
    if (cadence === 'WEEKLY')   return this.addDays(base, 7 * periods);
    if (cadence === 'BIWEEKLY') return this.addDays(base, 14 * periods);
    if (cadence === 'QUARTERLY') return this.addMonthsClamped(base, 3 * periods);
    return this.addMonthsClamped(base, periods);
  }

  private addDays(base: Date, days: number): Date {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return d;
  }

  // Matches .NET DateTime.AddMonths: day-of-month is clamped to the last day
  // of the target month (e.g., Jan 31 + 1 month → Feb 28/29, not March 3).
  private addMonthsClamped(base: Date, months: number): Date {
    const year = base.getFullYear();
    const month = base.getMonth() + months;
    const day = base.getDate();
    const targetYear = year + Math.floor(month / 12);
    const targetMonth = ((month % 12) + 12) % 12;
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    return new Date(targetYear, targetMonth, Math.min(day, lastDay));
  }

  public agreementChecked(state: CheckBoxState) {
    this.isAgreementChecked = !!state.valueOf();
    this.checkAgreements();
  }

  public bifurcationAgreementChecked(state: CheckBoxState) {
    this.isBifurcatedAgreementChecked = !!state.valueOf();
    this.checkAgreements();
  }

  private checkAgreements() {
    if (this.stripeFee > 0 && this.isBifurcated) {
      this.canFinalizePayment$.next(
        this.isAgreementChecked && this.isBifurcatedAgreementChecked,
      );
    } else if (this.stripeFee >= 0) {
      this.canFinalizePayment$.next(this.isAgreementChecked);
    } else if (
      environment.standalone &&
      this.route.snapshot.data['blankPayment']
    ) {
      const value = this.getRecaptchaValue();
      if (!value || value.trim() === '') this.canFinalizePayment$.next(false);
    } else {
      this.canFinalizePayment$.next(false);
    }
  }

  public setPaymentType(type: string, disclaimerAch: string) {
    this.paymentType = type;
    if (this.paymentType === 'card') {
      this.disclaimer = this.session?.merchant?.disclaimer;
      this.bifurcationDisclaimer = this.fee?.text;
    } else {
      this.disclaimer = this.session?.merchant?.disclaimerAch;
      this.bifurcationDisclaimer = this.feeAch?.text;
    }
  }

  public async stripeSubmit(event: Event) {
    event.preventDefault();
    // Early guard, this situation shouldn't happen
    if (!this.stripeElements) return;
    this.hideNotifications();

    this.loading$.next(true);

    const { error: submitError } = await this.stripeElements.submit();
    if (submitError) {
      this.displayNotification(
        submitError.message || 'unknown error, please try again later',
      );
      this.loading$.next(false);
      return;
    }

    this.stripe
      .createConfirmationToken({
        elements: this.stripeElements,
      })
      .pipe(
        mergeMap(({ error, confirmationToken }) => {
          if (error) {
            this.displayNotification(
              error.message || 'unknown error, please try again later',
            );

            throw new Error('error creating confirmation token');
          }

          return of(confirmationToken?.id);
        }),
        tap(
          (confirmationToken) => (this.confirmationToken = confirmationToken),
        ),
        mergeMap((confirmationToken) =>
          this.paymentService.paymentConfirmation({
            paymentIntentId: this.paymentIntentId,
            confirmationToken,
          }),
        ),
        mergeMap((confirmResult) => {
          if (!confirmResult?.status) {
            this.displayNotification('unknown error, please try again later');
            this.loading$.next(false);

            throw new Error('error submitting confirmation token to api');
          }

          return of(confirmResult as IPaymentConfirmationResponse);
        }),
        mergeMap((confirmResult) => {
          this.identifier = confirmResult.identifier;
          this.brand = confirmResult.brand;
          this.stripeFee = confirmResult.fee;
          this.stripeAmountCents = confirmResult.amountCents;
          this.recomputeSchedulePreview();
          this.isBifurcated = confirmResult.isBifurcated;
          this.setPaymentType(
            confirmResult.type,
            confirmResult.disclaimerAch,
          );

          if (confirmResult.status === 'succeeded') {
            this.stripeFinal$.next(true);
            return of(undefined);
          } else if (confirmResult.status === 'requires_action') {
            return this.stripe.handleNextAction({
              clientSecret: this.elementsOptions.clientSecret || '',
            });
          } else {
            this.displayNotification(
              confirmResult?.detail || 'unknown error, please try again later',
            );
            return of(undefined);
          }
        }),
        filter((isNextAction) => typeof isNextAction !== 'undefined'),
        tap((nextActionResult) => {
          if (nextActionResult?.error) {
            this.displayNotification(
              nextActionResult?.error?.message ||
                'unknown error, please try again later',
            );

            throw new Error('failed completing next action');
          }

          this.stripeFinal$.next(true);
        }),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe(() => console.log('submitted confirmation'));
  }

  public showPaymentMethod(event: Event) {
    event.preventDefault();

    this.createCustomerSession();
    this.stripeFinal$.next(false);
    this.stripeFee = -1;
    this.stripeAmountCents = -1;
    this.paymentSchedulePreview = [];
    this.checkAgreements();
  }

  public onTextBlur = (input: any) => {
    this.form.patchValue({
      [input.control.name]: input.value.trimStart().trimEnd(),
    });
    this.form.controls[input.control.name].markAllAsTouched();
  };

  public close(isSuccess = false) {
    if (this.environment.standalone && isSuccess) {
      this.receiptStep$.next(true);
      window.parent.postMessage({ type: "PaymentCompleted", data: { status: "SUCCESS"}}, environment.paymentApiUrl);
    } else {
      this.dialogOpened$.next(false);
      this.form.reset();
      this.reset();
    }
  }

  public cancel() {
    if (!this.loading$.value && !this.complete) {
      this.dialogOpened$.next(false);
      this.callback({ status: 'CANCEL' });
    } else if (this.complete) {
      this.dialogOpened$.next(false);
    }
  }

  public shouldDisplayClientInfo(
    key: keyof ISession['merchant']['clientInformation'],
  ) {
    const field = this.clientInformation
      ? this.clientInformation[key]
      : undefined;

    if (!field || typeof field === 'boolean') return false;

    return field?.show || field?.required || false;
  }

  public getFieldConfig(
    key: keyof ISession['merchant']['clientInformation'] | string,
  ) {
    const field = this.clientInformation
      ? this.clientInformation[
          key as keyof ISession['merchant']['clientInformation']
        ]
      : undefined;

    if (!field || typeof field === 'boolean')
      return {
        en: 'MISSING TRANSLATION',
        fr: 'MISSING TRANSLATION',
        required: true,
        readOnly: false,
        show:true,
      };

    return {
      ...field,
      en: field?.label?.en || 'MISSING TRANSLATION',
      fr: field?.label?.fr || 'MISSING TRANSLATION'
    };
  }

  public getAmount(): string {
    // After a successful submit, prefer the cents the back-end actually
    // charged. For a plan-selected checkout this is installment #1's amount
    // rather than the form-entered policy total. Falls back to the form
    // value during the pre-submit steps where stripeAmountCents is still the
    // -1 sentinel set by reset().
    if (this.stripeAmountCents > 0) {
      return this.toCurrency(this.stripeAmountCents / 100);
    }
    return this.toCurrency(
      parseFloat(this.form?.controls?.amount?.value.toString() || '0'),
    );
  }

  public getAmountCents(): number {
    const amountString = parseFloat(
      this.form?.controls?.amount?.value.toString() || '0',
    ).toFixed(2);
    return parseInt(amountString.replace('.', ''), 10);
  }

  public getStripeFee(): string {
    return this.toCurrency(this.stripeFee / 100);
  }

  public calculatePercent(amountCents: number): number {
    return amountCents * ((this.fee?.value?.percent || 0) / 100 || 0);
  }

  public calculateAmount(): number {
    return Math.floor((this.fee?.value?.amount || 0) * 100);
  }

  public getStripeTotal(): string {
    const amountCents = this.stripeAmountCents;
    let feeCents = this.stripeFee;

    return this.toCurrency((amountCents + feeCents) / 100);
  }

  // PPD-1132 — one-time fixed additional fee helpers
  public getOneTimeFeeCents(): number {
    const otf = this.session?.merchant?.oneTimeFee;
    if (!otf?.enabled || !(otf.amountCents > 0)) return 0;
    return otf.amountCents;
  }

  public getOneTimeFee(): string {
    return this.toCurrency(this.getOneTimeFeeCents() / 100);
  }

  public getOneTimeFeeName(): string {
    return this.session?.merchant?.oneTimeFee?.name || 'Additional Fee';
  }

  // OTF only applies to tokenized payments (card saved) or re-use of a saved token,
  // and only if the customer has not already been charged OTF by this merchant.
  public isOtfApplicable(): boolean {
    if (this.getOneTimeFeeCents() <= 0) return false;
    if (this.otfAlreadyCharged) return false;
    const isPaymentWithToken = this.session?.customerSession?.paymentWithToken === true;
    const hasScheduleSelected = this.selectedPaymentScheduleRuleId !== null;
    return isPaymentWithToken || hasScheduleSelected;
  }

  // "Premium Paid" on the receipt = stripeAmountCents minus OTF (backend bundles OTF into base amount)
  public getBaseAmount(): string {
    if (this.stripeAmountCents > 0) {
      return this.toCurrency((this.stripeAmountCents - this.getApplicableOneTimeFeeCents()) / 100);
    }
    return this.getAmount();
  }

  // Pre-submit estimated total: form-entered policy amount + one-time fee (service fee not yet known)
  public getPreSubmitEstimatedTotal(): string {
    return this.toCurrency((this.getAmountCents() + this.getApplicableOneTimeFeeCents()) / 100);
  }

  // Pre-submit total once service fee is confirmed: stripeAmountCents (policy) + service fee + OTF.
  // stripeAmountCents at this stage comes from Stripe Elements and does not yet include OTF
  // (OTF is only added to the charge amount at confirmation time by the backend).
  public getPreSubmitStripeTotalWithOtf(): string {
    return this.toCurrency((this.stripeAmountCents + this.stripeFee + this.getApplicableOneTimeFeeCents()) / 100);
  }

  private getApplicableOneTimeFeeCents(): number {
    return this.isOtfApplicable() ? this.getOneTimeFeeCents() : 0;
  }

  public async open(
    properties?: { [key: string]: string | { [key: string]: string } },
    callback?: () => void,
  ) {
    if (properties?.amount) properties.amount = properties.amount.toString();
    properties = Object.assign(
      {
        amount: this.amount.toString(),
        policyId: this.attrPolicyId,
        language: this.attrLanguage || 'en',
        referenceNumber: this.attrReferenceNumber,
        clientInformation: Object.assign(
          {
            firstName: this.attrFirstName,
            lastName: this.attrLastName,
            insuredName: this.attrInsuredName,
            email: this.attrEmail,
            address: this.attrAddress,
            city: this.attrCity,
            province: this.attrProvince,
            zip: this.attrZip,
            customerId: this.attrCustomerId,
          },
          properties?.clientInformation,
        ),
        custom: Object.assign(
          {
            pif: this.attrCustomPif,
            dp: this.attrCustomDp,
            pmt: this.attrCustomPmt,
            programcode: this.attrCustomProgramcode,
            balancedue: this.attrCustomBalancedue,
          },
          properties?.custom,
        ),
      },
      properties || {},
    );

    await this.initSession(this.session);

    if (!!callback) {
      this.callback = callback;
    }

    Object.entries(properties).forEach(([key, value]) => {
      if (typeof value !== 'string') return;

      if (key === 'policyId') {
        if (this?.policyId?.omit.allow && this.policyId?.omit.quantity) {
          this.originalPolicyId = value as string;
          value = value.slice(this.policyId.omit.quantity);
        }
        this.stashedPolicyId = value;
      }

      if (key === 'language') {
        this.currentLanguage = value as LANGUAGES;
        this.languageService.setLanguage(value as LANGUAGES);
      }

      this.form.get(key)?.setValue(value);
      if (key === 'amount') this.amount = value.toString();
    });

    if (typeof properties.clientInformation === 'object') {
      Object.entries(properties.clientInformation).forEach(([key, value]) => {
        this.form.get(key)?.setValue(value);
      });
    }

    if (typeof properties.custom === 'object') {
      this.stashedCustom = properties.custom;
      this.form.addControl('custom', new FormControl(properties.custom));
    }

    this.dialogOpened$.next(true);

    if (this.rootElement) {
      const scrollElement =
        this.rootElement.element.nativeElement.querySelector(
          '.k-window.k-dialog .k-window-content.k-dialog-content',
        );
      if (scrollElement) {
        scrollElement.scroll(0, 0);
      }
    }
  }

  public translate = (field: { en?: string; fr?: string } | undefined) => {
    if (this.currentLanguage === LANGUAGES.french)
      return field?.fr || field?.en || 'MISSING TRANSLATION';
    return field?.en || 'MISSING TRANSLATION';
  };

  public verifyPinTranslate() {
    return 'Enter pin';
  }

  public setLanguage(language: string) {
    this.currentLanguage = language as LANGUAGES;
    this.languageService.setLanguage(language as LANGUAGES);
  }

  public speedbumpVerify() {
    this.loading$.next(true);

    if (this.pinForm.valid) {
      let pin = this.pinForm.get('pin');
      pin?.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]{6}$'),
      ]);

      this.pinVerificationService
        .createPinVerification(this.pinForm.value.email)
        .pipe(
          tap((result) => {
            if (result && result?.pinId && result?.pinId !== '') {
              this.pinId = result.pinId;

              this.showVerifyPin$.next(true);
            } else {
              this.displayNotification(
                'could not begin verification, please try again later',
              );
            }
          }),
          catchError((err) => {
            console.error(err);

            this.displayNotification(
              'Could not begin verification. Please check your network connection and try again.',
            );

            return err;
          }),
          finalize(() => {
            this.loading$.next(false);
          }),
        )
        .subscribe();
    }
  }

  public pinVerify() {
    this.loading$.next(true);

    if (this.pinForm.valid) {
      this.pinVerificationService
        .confirmPinVerification(
          this.pinForm.value.email,
          this.pinId,
          this.pinForm.value.pin,
        )
        .pipe(
          tap((result) => {
            if (result && result?.success == true) {
              this.form.controls.email?.setValue(this.pinForm.value.email);

              this.showSpeedbump$.next(false);
            } else {
              this.displayNotification(
                'Could not verify provided pin. Please confirm your pin.',
              );
            }
          }),
          catchError((err) => {
            console.error(err);

            this.displayNotification(
              'Could not verify provided pin. Please confirm your pin.',
            );

            return err;
          }),
          finalize(() => {
            this.loading$.next(false);
          }),
        )
        .subscribe();
    }
  }

  public shouldDisplayFee() {
    return this.fee?.status;
  }

  public pinBlur(_event: Event) {}

  public showClient(event: any) {
    event.preventDefault();

    if (!this.loading$.value) {
      this.stripeStep$.next(false);
      this.scheduleStep$.next(false);
      this.stripeFinal$.next(false);
      this.pfStep$.next(false);
      this.selectedPaymentMethodType = 'stripe';
      this.stripeFee = -1;
      this.stripeAmountCents = -1;
      this.paymentSchedulePreview = [];
      this.checkAgreements();
    }
  }

  public toCurrency(value: number) {
    return this.intl.formatNumber(value, 'c', 'en');
  }

  /** Copy the payment URL to the clipboard; give feedback via button label + notification. */
  public async copyUrl(): Promise<void> {
    const url = window.location.href.split('?')[0];
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(url);
      this.copyText = 'URL Copied!';
      setTimeout(() => { this.copyText = 'Copy Payment URL'; }, 2000);
      this.displayNotification('URL copied to clipboard', 'success');
    } catch {
      this.displayNotification(
        'Could not copy automatically. Please select and copy the URL above manually.',
        'error',
      );
    }
  }

  private displayNotification(content: string, style: 'error' | 'success' | 'warning' | 'info' | 'none' = 'error') {
    let notificationRef = this.notificationService.show({
      content,
      appendTo: this.notificationContainer,
      position: { horizontal: 'center', vertical: 'bottom' },
      type: { style, icon: true },
      closable: true,
    });
    let notificationId = notificationRef?.notification?.instance?.contentId ?? "typescripthell";
    this.notificationTracker[notificationId] = notificationRef;
    notificationRef?.afterHide?.subscribe(() => delete this.notificationTracker[notificationId]);
    setTimeout(() => notificationRef.hide(), 5000);
  }

  private hideNotifications() { 
    Object.keys(this.notificationTracker).forEach(key => {
      if (this.notificationTracker[key]?.hide) {
        this.notificationTracker[key]?.hide();
      }
    })
  }

  protected readonly undefined = undefined;
  protected readonly Object = Object;
}
